import { useResizeObserver } from "@reactuses/core";
import type { ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../../lib/cn";
import { Tag } from "../../Tag";
import { useMessage } from "./Context";
import { BubbleSurface } from "./Surface";

type MessageBubbleProps = {
    children: ReactNode;
    variant?: "default" | "standalone" | "continuation";
    className?: string;
};

export function MessageBubble({
    children,
    variant = "default",
    className,
}: MessageBubbleProps) {
    const { sender, label } = useMessage();
    const hasLabel = Boolean(label) && variant !== "continuation";
    const bubbleRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const [canAlignLabelLeft, setCanAlignLabelLeft] = useState(false);

    function updateLabelAlignment() {
        const bubble = bubbleRef.current;
        const labelElement = labelRef.current;

        if (sender !== "member" || !hasLabel || !bubble || !labelElement) {
            return;
        }

        setCanAlignLabelLeft(doesLabelFitInsideBubble(bubble, labelElement));
    }

    useLayoutEffect(updateLabelAlignment);
    useResizeObserver(bubbleRef, updateLabelAlignment);
    useResizeObserver(labelRef, updateLabelAlignment);

    return (
        <BubbleSurface
            ref={bubbleRef}
            hasLabel={hasLabel}
            shape={variant === "standalone" ? "rounded" : "message"}
            className={className}
        >
            {label && variant !== "continuation" ? (
                <Tag
                    ref={labelRef}
                    variant="primary"
                    className={cn(
                        "absolute -top-12",
                        sender === "member" && !canAlignLabelLeft
                            ? "right-12 min-w-min"
                            : "left-12",
                    )}
                >
                    {label}
                </Tag>
            ) : null}

            {children}
        </BubbleSurface>
    );
}

function doesLabelFitInsideBubble(
    bubble: HTMLDivElement,
    label: HTMLSpanElement,
): boolean {
    const bubbleStyles = getComputedStyle(bubble);
    const horizontalInset = Number.parseFloat(bubbleStyles.paddingInlineStart);
    const availableWidth = bubble.clientWidth - horizontalInset * 2;

    return label.offsetWidth <= availableWidth;
}
